import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Mail, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useReferralTemplates } from '@/hooks/useReferralTemplates';
import { useCrmUsers } from '@/hooks/crm/useCrmUsers';

interface BulkInvitationManagerProps {
  maxInvites: number;
}

const BulkInvitationManager: React.FC<BulkInvitationManagerProps> = ({ maxInvites }) => {
  const { toast } = useToast();
  const { templates } = useReferralTemplates();
  const { users, sendReferralEmail } = useCrmUsers();
  
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  const uninvitedUsers = users.filter(user => 
    user.status === 'new' && user.referralHistory.length === 0
  );

  const handleSendBulkInvitations = async () => {
    if (!selectedTemplateId) {
      toast({
        title: "Template Required",
        description: "Please select an email template before sending bulk invitations.",
        variant: "destructive",
      });
      return;
    }

    const usersToInvite = uninvitedUsers.slice(0, maxInvites);
    
    if (usersToInvite.length === 0) {
      toast({
        title: "No Users to Invite",
        description: "There are no new users to send invitations to.",
        variant: "default",
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setProcessedCount(0);
    setSuccessCount(0);
    setFailedCount(0);

    const templateName = templates.find(t => t.id === selectedTemplateId)?.name || 'Selected Template';

    for (let i = 0; i < usersToInvite.length; i++) {
      const user = usersToInvite[i];
      try {
        await sendReferralEmail(user.id, selectedTemplateId, templateName);
        setSuccessCount(prev => prev + 1);
      } catch (error) {
        console.error(`Error sending invitation to ${user.email}:`, error);
        setFailedCount(prev => prev + 1);
      }
      
      setProcessedCount(i + 1);
      setProgress(Math.round(((i + 1) / usersToInvite.length) * 100));
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsProcessing(false);
    
    toast({
      title: "Bulk Invitation Complete",
      description: `Successfully sent ${successCount} invitations. ${failedCount} failed.`,
      variant: failedCount > 0 ? "default" : "default",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Bulk Invitation Manager
        </CardTitle>
        <CardDescription>
          Send invitations to new users who haven't been invited yet
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email Template</label>
            <Select
              value={selectedTemplateId}
              onValueChange={setSelectedTemplateId}
              disabled={isProcessing}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an email template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} {template.isDefault && '(Default)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Users to invite: <span className="font-semibold">{uninvitedUsers.length}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Daily limit: <span className="font-semibold">{maxInvites}</span>
            </div>
          </div>
          
          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Processing: {processedCount}/{uninvitedUsers.length}</span>
                <span>{progress}% complete</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="flex gap-2 items-center">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Automatic invites send daily
          </span>
        </div>
        <Button 
          onClick={handleSendBulkInvitations} 
          disabled={isProcessing || uninvitedUsers.length === 0}
          className="flex items-center gap-2"
        >
          <Mail className="h-4 w-4" />
          {isProcessing ? 'Sending...' : 'Send Invitations Now'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default BulkInvitationManager;
